/**
 * useProjectDefaultAIModel — project-level default text model.
 *
 * Reads `projects/{projectId}.defaultAiModel`, the same loose Firestore field
 * used by content-plan model selectors. Falls back to the current Gemini text
 * default so new AI surfaces do not silently fall back to a built-in Anthropic
 * model when the project has not persisted a preference yet.
 */

import { useEffect, useState } from 'react';
import {
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { normalizeModelId } from '@modules/ui-components';

export const PROJECT_DEFAULT_AI_MODEL = 'gemini-3.8-flash' as const;

export interface UseProjectDefaultAIModelResult {
  value: string;
  loading: boolean;
  setDefault: (model: string) => Promise<void>;
}

export function useProjectDefaultAIModel(
  projectId: string | null | undefined,
): UseProjectDefaultAIModelResult {
  const [value, setValue] = useState<string>(PROJECT_DEFAULT_AI_MODEL);
  const [loading, setLoading] = useState<boolean>(Boolean(projectId));

  useEffect(() => {
    if (!projectId) {
      setValue(PROJECT_DEFAULT_AI_MODEL);
      setLoading(false);
      return;
    }

    setLoading(true);
    const db = CoreFirebaseService.getInstance().firestore;
    const ref = doc(db, 'projects', projectId);
    const unsubscribe = onSnapshot(ref, (snap) => {
      const data = snap.data() as { defaultAiModel?: string } | undefined;
      setValue(normalizeModelId(data?.defaultAiModel || PROJECT_DEFAULT_AI_MODEL));
      setLoading(false);
    }, () => {
      setValue(PROJECT_DEFAULT_AI_MODEL);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [projectId]);

  const setDefault = async (model: string): Promise<void> => {
    if (!projectId) return;
    const db = CoreFirebaseService.getInstance().firestore;
    const ref = doc(db, 'projects', projectId);
    await updateDoc(ref, {
      defaultAiModel: normalizeModelId(model),
      updatedAt: serverTimestamp(),
    });
  };

  return { value, loading, setDefault };
}
